import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiX, FiFileText, FiExternalLink } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';

const ST = {
  pending: { background: '#FDF3E3', color: '#A87B2D' },
  verified: { background: '#E9F3EC', color: '#3B7A4E' },
  rejected: { background: '#FBEEEA', color: '#B4432E' },
};

export default function Warranty() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try { setRows(await api('/warranty' + (filter ? '?status=' + filter : ''))); } catch (e) { setToast(e.message); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const pending = rows ? rows.filter((r) => r.status === 'pending').length : 0;

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Warranty</div>
          <h1>Applications</h1>
          <div className="count">{rows ? `${pending} pending · ${rows.length} shown` : 'Loading…'}</div>
        </div>
      </div>

      <div className="filter-bar">
        {['', 'pending', 'verified', 'rejected'].map((s) => (
          <button key={s} className={'role-tab' + (filter === s ? ' active' : '')} onClick={() => setFilter(s)}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div className="table-card">
        {!rows ? <div className="empty">Loading…</div> :
         rows.length === 0 ? <div className="empty"><h3>No applications</h3><p>Warranty applications from the public form appear here.</p></div> :
          <table className="data">
            <thead><tr>
              <th>Customer</th><th>Brand</th><th>Dealer</th><th>Bill</th><th>Status</th><th>Certificate</th><th>Sent</th>
            </tr></thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => nav('/m/warranty_applications/' + w.id)}>
                  <td style={{ fontWeight: 600 }}>{w.customer_name}<div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>{w.customer_contact || w.customer_email || ''}</div></td>
                  <td style={{ color: 'var(--muted)' }}>{w.brand}</td>
                  <td style={{ color: 'var(--muted)' }}>{w.dealer_name || <span style={{ color: '#B4432E' }}>needs filling</span>}</td>
                  <td>{w.bill_file ? <FiFileText style={{ color: '#3B7A4E' }} /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                  <td><span className="pill" style={ST[w.status]}>{w.status}</span></td>
                  <td style={{ color: 'var(--muted)' }}>{w.certificate_no || '—'}</td>
                  <td>{w.status === 'verified' ? `${w.cert_sent_whatsapp ? 'W' : ''}${w.cert_sent_email ? 'E' : ''}` || '—' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </div>
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export function WarrantyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const me = useMe();
  const perm = findPerm(me.modules, 'warranty_applications');
  const [data, setData] = useState(null);
  const [dealer, setDealer] = useState({ dealer_name: '', dealer_contact: '' });
  const [toast, setToast] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api('/warranty/' + id);
      setData(d);
      setDealer({ dealer_name: d.application.dealer_name || '', dealer_contact: d.application.dealer_contact || '' });
    } catch (e) { setToast(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  async function saveDealer() {
    try { await api('/warranty/' + id, { method: 'PUT', body: dealer }); flash('Saved'); load(); }
    catch (e) { flash(e.message); }
  }
  async function verify() {
    if (!dealer.dealer_name.trim()) { flash('Fill dealer name before verifying'); return; }
    await saveDealer();
    try { const r = await api('/warranty/' + id + '/verify', { method: 'POST' }); flash('Verified · ' + r.certificate_no); load(); }
    catch (e) { flash(e.message); }
  }
  async function reject(reason) {
    try { await api('/warranty/' + id + '/reject', { method: 'POST', body: { reason } }); flash('Rejected'); setRejecting(false); load(); }
    catch (e) { flash(e.message); }
  }
  async function toggleSent(channel, value) {
    try { await api('/warranty/' + id + '/sent', { method: 'PATCH', body: { channel, value } }); load(); }
    catch (e) { flash(e.message); }
  }

  if (!data) return <div className="empty">{toast || 'Loading…'}</div>;
  const w = data.application;
  const billUrl = w.bill_file ? '/api/warranty-files/' + w.bill_file : null;
  const waLink = w.customer_contact ? `https://wa.me/${w.customer_contact.replace(/[^0-9]/g, '')}` : null;
  const mailLink = w.customer_email ? `mailto:${w.customer_email}?subject=Your ${w.brand} Warranty Certificate ${w.certificate_no || ''}` : null;

  return (
    <div>
      <button className="btn btn-sm" style={{ marginBottom: 14 }} onClick={() => nav('/m/warranty_applications')}><FiArrowLeft /> All applications</button>

      <div className="camp-summary">
        <div className="camp-main">
          <div className="page-eyebrow">{w.brand} · Warranty application</div>
          <h1 className="page-title" style={{ margin: '6px 0 8px' }}>{w.customer_name}</h1>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 14, marginTop: 12 }}>
            <Field label="Contact" value={w.customer_contact} />
            <Field label="Email" value={w.customer_email} />
            <Field label="Site address" value={w.site_address} />
            <Field label="Date of despatch" value={w.date_of_despatch ? w.date_of_despatch.slice(0,10) : null} />
            <Field label="Fabricator" value={w.fabricator_name} />
            <Field label="Fabricator contact" value={w.fabricator_contact} />
          </div>
          {data.items.length > 0 && (
            <table className="data" style={{ marginTop: 18, border: '1px solid var(--line)', borderRadius: 8 }}>
              <thead><tr><th>Colour code</th><th>Description</th><th>Qty</th></tr></thead>
              <tbody>{data.items.map((it) => <tr key={it.id}><td>{it.colour_code || '—'}</td><td>{it.description || '—'}</td><td>{it.quantity || '—'}</td></tr>)}</tbody>
            </table>
          )}
        </div>

        <div className="camp-stats">
          <div className="stat">
            <div className="stat-label">Status</div>
            <div style={{ marginTop: 6 }}><span className="pill" style={ST[w.status]}>{w.status}</span></div>
            {w.certificate_no && <div style={{ marginTop: 10, fontSize: 13 }}>Certificate: <strong>{w.certificate_no}</strong></div>}
          </div>
          <div className="stat">
            <div className="stat-label">Attached bill</div>
            {billUrl
              ? <a className="btn btn-sm" href={billUrl} target="_blank" rel="noreferrer" style={{ marginTop: 8 }}><FiFileText /> View bill <FiExternalLink /></a>
              : <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6 }}>No bill attached</div>}
          </div>
        </div>
      </div>

      {/* Staff actions */}
      {w.status === 'pending' && perm.can_edit && (
        <div className="table-card" style={{ padding: 22, marginTop: 22 }}>
          <h2 style={{ fontSize: 17, margin: '0 0 14px' }}>Verify & issue</h2>
          <div className="grid2">
            <div className="field"><label>Dealer name</label>
              <input value={dealer.dealer_name} onChange={(e) => setDealer({ ...dealer, dealer_name: e.target.value })} placeholder="Confirm the dealer" /></div>
            <div className="field"><label>Dealer contact</label>
              <input value={dealer.dealer_contact} onChange={(e) => setDealer({ ...dealer, dealer_contact: e.target.value })} placeholder="Phone" /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn btn-dark" onClick={verify}><FiCheck /> Verify & issue certificate</button>
            <button className="btn btn-danger" onClick={() => setRejecting(true)}><FiX /> Reject</button>
          </div>
          {rejecting && (
            <div style={{ marginTop: 14 }}>
              <input id="rej" className="" placeholder="Reason for rejection" style={{ width: '100%', padding: 11, border: '1px solid var(--line)', borderRadius: 9 }} />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button className="btn btn-danger btn-sm" onClick={() => reject(document.getElementById('rej').value)}>Confirm reject</button>
                <button className="btn btn-sm" onClick={() => setRejecting(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {w.status === 'verified' && (
        <div className="table-card" style={{ padding: 22, marginTop: 22 }}>
          <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Send certificate</h2>
          <p className="page-desc" style={{ fontSize: 13, marginTop: 0 }}>Send the certificate manually via WhatsApp / email, then tick when done.</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
            {waLink && <a className="btn btn-sm" href={waLink} target="_blank" rel="noreferrer">Open WhatsApp <FiExternalLink /></a>}
            {mailLink && <a className="btn btn-sm" href={mailLink}>Open Email <FiExternalLink /></a>}
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={w.cert_sent_whatsapp} onChange={(e) => toggleSent('whatsapp', e.target.checked)} /> Sent via WhatsApp
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={w.cert_sent_email} onChange={(e) => toggleSent('email', e.target.checked)} /> Sent via Email
            </label>
          </div>
        </div>
      )}

      {w.status === 'rejected' && (
        <div className="table-card" style={{ padding: 22, marginTop: 22, color: '#B4432E' }}>
          Rejected{w.reject_reason ? ` — ${w.reject_reason}` : ''}
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function Field({ label, value }) {
  return <div><span style={{ color: 'var(--muted)', fontSize: 12 }}>{label}</span><div style={{ fontWeight: 500 }}>{value || '—'}</div></div>;
}
